import { useCallback, useEffect, useMemo, useState } from 'react';

export interface QueuePair {
  key: string;
  front: File;
  back: File | null;
}

export interface MatchedItem {
  id: string;
  title: string | null;
  set_name: string | null;
  card_number: string | null;
  stock_image_url: string | null;
  thumb_url: string | null;
  photo_count: number;
}

export type BulkMatchDecision =
  | { kind: 'matched'; item: MatchedItem }
  | { kind: 'draft' }
  | { kind: 'skip' };

export interface BulkMatchQueueSummary {
  attaching: number;
  draft: number;
  skipped: number;
}

export interface UseBulkMatchQueueResult {
  index: number;
  total: number;
  current: QueuePair | null;
  isDone: boolean;
  decisions: Map<string, BulkMatchDecision>;
  summary: BulkMatchQueueSummary;
  attach: (item: MatchedItem) => void;
  draft: () => void;
  skip: () => void;
  swap: () => void;
  back: () => void;
  goTo: (i: number) => void;
  reset: () => void;
}

/** One-at-a-time queue over Bulk Photo Upload pairs, mirroring useBulkListPriceQueue. */
export function useBulkMatchQueue(
  pairs: QueuePair[],
  active: boolean,
): UseBulkMatchQueueResult {
  const [queue, setQueue] = useState<QueuePair[]>(pairs);
  const [index, setIndex] = useState(0);
  const [decisions, setDecisions] = useState<Map<string, BulkMatchDecision>>(new Map());

  const pairsKey = useMemo(() => pairs.map((p) => p.key).join(','), [pairs]);

  useEffect(() => {
    if (active) {
      setQueue(pairs);
      setIndex(0);
      setDecisions(new Map());
    }
  }, [active, pairsKey]);

  const total = queue.length;
  const current = index < total ? queue[index] : null;
  const isDone = index >= total;

  const record = useCallback(
    (decision: BulkMatchDecision) => {
      setDecisions((prev) => {
        const item = queue[index];
        if (!item) return prev;
        const next = new Map(prev);
        next.set(item.key, decision);
        return next;
      });
      setIndex((i) => Math.min(i + 1, total));
    },
    [queue, index, total],
  );

  const attach = useCallback((item: MatchedItem) => record({ kind: 'matched', item }), [record]);
  const draft = useCallback(() => record({ kind: 'draft' }), [record]);
  const skip = useCallback(() => record({ kind: 'skip' }), [record]);

  const swap = useCallback(() => {
    setQueue((prev) => {
      const next = [...prev];
      const item = next[index];
      if (!item || !item.back) return prev;
      next[index] = { ...item, front: item.back, back: item.front };
      return next;
    });
  }, [index]);

  const back = useCallback(() => {
    setIndex((i) => {
      const prev = Math.max(0, i - 1);
      setDecisions((d) => {
        const item = queue[prev];
        if (!item) return d;
        const next = new Map(d);
        next.delete(item.key);
        return next;
      });
      return prev;
    });
  }, [queue]);

  const goTo = useCallback(
    (i: number) => setIndex(Math.max(0, Math.min(i, total))),
    [total],
  );

  const reset = useCallback(() => {
    setIndex(0);
    setDecisions(new Map());
  }, []);

  const summary = useMemo<BulkMatchQueueSummary>(() => {
    let attaching = 0;
    let draft = 0;
    let skipped = 0;
    for (const d of decisions.values()) {
      if (d.kind === 'matched') attaching += 1;
      else if (d.kind === 'draft') draft += 1;
      else skipped += 1;
    }
    return { attaching, draft, skipped };
  }, [decisions]);

  return { index, total, current, isDone, decisions, summary, attach, draft, skip, swap, back, goTo, reset };
}
